/**
 * Colunas da tabela de Itens de Trabalho: QUAIS, em QUE ORDEM, com QUE LARGURA
 * e por qual delas a lista está ordenada (SPEC-20261003-0002).
 *
 * Mora fora da tela porque é a parte que se pode conferir sozinha: reconciliar
 * uma preferência velha, mover uma coluna de lugar, decidir quem vem antes.
 * Dentro do componente isso só se verificava abrindo o navegador.
 */

/**
 * `minimo` é o piso do arraste. Sem ele a pessoa estreita uma coluna até sumir
 * e depois não tem mais onde pegar para alargá-la de volta.
 *
 * TÍTULO é a primeira e não se esconde: é onde moram o código do item, o recuo
 * da árvore e o cursor que abre e fecha os filhos.
 */
export const COLUNAS_DA_TABELA = [
  { key: 'titulo', rotulo: 'Título', largura: 320, minimo: 200, padrao: true },
  { key: 'status', rotulo: 'Status', largura: 132, minimo: 96, padrao: true },
  { key: 'responsavel', rotulo: 'Responsável', largura: 180, minimo: 110, padrao: true },
  { key: 'prazo', rotulo: 'Prazo', largura: 108, minimo: 80, padrao: true },
  { key: 'progresso', rotulo: 'Progresso', largura: 136, minimo: 90, padrao: true },
  { key: 'prioridade', rotulo: 'Prioridade', largura: 120, minimo: 90, padrao: false },
  { key: 'etapa', rotulo: 'Etapa', largura: 150, minimo: 90, padrao: false },
  { key: 'inicio', rotulo: 'Início', largura: 108, minimo: 80, padrao: false },
  { key: 'tipo', rotulo: 'Tipo', largura: 130, minimo: 90, padrao: false },
  { key: 'empresa', rotulo: 'Empresa', largura: 160, minimo: 100, padrao: false },
  { key: 'area', rotulo: 'Área', largura: 150, minimo: 100, padrao: false },
  { key: 'portfolio', rotulo: 'Portfólio', largura: 170, minimo: 100, padrao: false },
  { key: 'conclusao', rotulo: 'Concluído em', largura: 120, minimo: 90, padrao: false },
  { key: 'criado', rotulo: 'Criado em', largura: 120, minimo: 90, padrao: false },
  { key: 'atualizado', rotulo: 'Atualizado em', largura: 128, minimo: 90, padrao: false },
] as const

/** Uma coluna, do catálogo base ou de um campo complementar. */
export type DefinicaoDeColuna = {
  key: string
  rotulo: string
  largura: number
  minimo: number
  padrao: boolean
  /** Campo complementar: a célula lê de `customValues`, e não do item. */
  complementar?: boolean
  /** Coluna de checkbox da trilha: uma etapa ou um status (SPEC-20261007-2155-trilha-de-etapas-e-status). */
  trilha?: 'etapa' | 'status'
}

/**
 * Prefixo das colunas de campo complementar (SPEC-20261006-1057-colunas-da-lista).
 *
 * Separa os dois mundos numa chave só: `status` é campo do item, `cf:sla` é
 * campo complementar. Sem o prefixo, um campo complementar chamado "status"
 * apagaria a coluna de verdade do item.
 */
export const PREFIXO_COMPLEMENTAR = 'cf:'

/**
 * Colunas da TRILHA (SPEC-20261007-2155-trilha-de-etapas-e-status): uma por etapa de fluxo e uma
 * por status, com checkbox — marcado = o item já passou por ali.
 */
export const PREFIXO_ETAPA = 'etapa:'
export const PREFIXO_STATUS = 'status:'

/**
 * Colunas que dependem de um cadastro que chega por outra consulta (campos
 * complementares, fluxos, status). Enquanto ele não chega, a preferência
 * guardada não pode descartá-las.
 */
const PREFIXOS_TARDIOS = [PREFIXO_COMPLEMENTAR, PREFIXO_ETAPA, PREFIXO_STATUS]

/**
 * O catálogo da vez: as colunas do item mais uma por campo complementar.
 *
 * É função, e não constante, porque os campos complementares são cadastrados
 * pelo workspace — mudam sem ninguém recompilar nada.
 */
export function catalogoDeColunas(
  complementares: readonly { key: string; rotulo: string }[] = [],
  /** Etapas dos fluxos e status do workspace: as colunas da trilha. */
  trilha: { etapas: readonly { id: string; rotulo: string }[]; status: readonly { id: string; rotulo: string }[] } = {
    etapas: [],
    status: [],
  },
): DefinicaoDeColuna[] {
  return [
    ...COLUNAS_DA_TABELA.map((c) => ({ ...c })),
    ...complementares.map((c) => ({
      key: `${PREFIXO_COMPLEMENTAR}${c.key}`,
      rotulo: c.rotulo,
      largura: 150,
      minimo: 90,
      padrao: false,
      complementar: true,
    })),
    ...trilha.etapas.map((e) => ({
      key: `${PREFIXO_ETAPA}${e.id}`,
      rotulo: `Etapa · ${e.rotulo}`,
      largura: 120,
      minimo: 80,
      padrao: false,
      trilha: 'etapa' as const,
    })),
    ...trilha.status.map((s) => ({
      key: `${PREFIXO_STATUS}${s.id}`,
      rotulo: `Status · ${s.rotulo}`,
      largura: 120,
      minimo: 80,
      padrao: false,
      trilha: 'status' as const,
    })),
  ]
}

export type ColunaKey = string

/** Lista ordenada, e não um mapa de ligado/desligado: a ordem é parte da
 *  escolha — num mapa ela viveria implícita na ordem de escrita do objeto, que
 *  ninguém controla. */
export type EstadoColuna = { k: ColunaKey; on: boolean; w: number }
export type Ordenacao = { k: ColunaKey; dir: 'asc' | 'desc' } | null
/** `v` é a versão do formato: o que vier de outra versão é descartado. */
export type EstadoTabela = {
  v: 1
  cols: EstadoColuna[]
  ord: Ordenacao
  /** Colunas "para todos" que a pessoa já recebeu (SPEC-20261103-1217) — ver `colunas-do-filtro.ts`. */
  todos?: string[]
}

export const definicaoDaColuna = (k: ColunaKey, catalogo: readonly DefinicaoDeColuna[]) =>
  catalogo.find((c) => c.key === k)
export const rotuloDaColuna = (k: ColunaKey, catalogo: readonly DefinicaoDeColuna[]) =>
  definicaoDaColuna(k, catalogo)?.rotulo ?? k

export const padraoDaTabela = (catalogo: readonly DefinicaoDeColuna[]): EstadoTabela => ({
  v: 1,
  cols: catalogo.map((c) => ({ k: c.key, on: c.padrao, w: c.largura })),
  ord: null,
})

/**
 * Entrada do que estava gravado. O valor vem do banco como JSON solto e pode
 * ser de uma versão anterior da tela: coluna que não existe mais sai, coluna
 * nova entra no fim, largura abaixo do piso é puxada de volta.
 *
 * TÍTULO é forçado para o primeiro lugar e para ligado — uma preferência velha
 * não pode deixar a lista sem o que identifica a linha.
 *
 * Devolve `null` quando não dá para aproveitar nada: aí vale o padrão, em vez
 * de a tela quebrar por causa de uma preferência velha.
 */
export function adotarTabela(
  valor: unknown,
  catalogo: readonly DefinicaoDeColuna[] = COLUNAS_DA_TABELA.map((c) => ({ ...c })),
  /**
   * Segura as colunas de campo complementar que o catálogo ainda não conhece.
   *
   * Os campos complementares chegam por outra consulta, depois da preferência:
   * sem isto, a primeira adoção descartava as colunas deles — e, como o estado
   * já nascia sem elas, não voltavam mais. Quem chama liga enquanto a consulta
   * está em voo e desliga quando ela chega, para então limpar de verdade o que
   * foi excluído do workspace.
   */
  segurarComplementares = false,
): EstadoTabela | null {
  if (typeof valor !== 'object' || valor === null) return null
  const bruto = valor as { v?: unknown; cols?: unknown; ord?: unknown; todos?: unknown }
  if (bruto.v !== 1 || !Array.isArray(bruto.cols)) return null

  const conhecidas = new Map<string, DefinicaoDeColuna>(catalogo.map((c) => [c.key, c]))
  const vistas = new Set<string>()
  const cols: EstadoColuna[] = []
  for (const item of bruto.cols) {
    if (typeof item !== 'object' || item === null) continue
    const c = item as { k?: unknown; on?: unknown; w?: unknown }
    const def = typeof c.k === 'string' ? conhecidas.get(c.k) : undefined
    if (!def) {
      // coluna de campo complementar cujo catálogo ainda não chegou
      if (
        segurarComplementares &&
        typeof c.k === 'string' &&
        PREFIXOS_TARDIOS.some((prefixo) => (c.k as string).startsWith(prefixo)) &&
        !vistas.has(c.k)
      ) {
        vistas.add(c.k)
        const w = typeof c.w === 'number' && Number.isFinite(c.w) ? c.w : 150
        cols.push({ k: c.k, on: c.on !== false, w: Math.max(90, Math.round(w)) })
      }
      continue
    }
    if (vistas.has(def.key)) continue
    vistas.add(def.key)
    const w = typeof c.w === 'number' && Number.isFinite(c.w) ? c.w : def.largura
    cols.push({ k: def.key, on: c.on !== false, w: Math.max(def.minimo, Math.round(w)) })
  }
  for (const def of catalogo) {
    if (!vistas.has(def.key)) cols.push({ k: def.key, on: def.padrao, w: def.largura })
  }
  const i = cols.findIndex((c) => c.k === 'titulo')
  if (i > 0) cols.unshift(cols.splice(i, 1)[0]!)
  if (cols[0]) cols[0] = { ...cols[0], on: true }

  let ord: Ordenacao = null
  if (typeof bruto.ord === 'object' && bruto.ord !== null) {
    const o = bruto.ord as { k?: unknown; dir?: unknown }
    if (typeof o.k === 'string' && conhecidas.has(o.k) && (o.dir === 'asc' || o.dir === 'desc')) {
      ord = { k: o.k as ColunaKey, dir: o.dir }
    }
  }
  const todos = Array.isArray(bruto.todos)
    ? bruto.todos.filter((k): k is string => typeof k === 'string')
    : undefined
  return todos ? { v: 1, cols, ord, todos } : { v: 1, cols, ord }
}

/** O que a escolha de colunas era antes de ir para a conta: `[{key,on}]`. */
export function adotarEscolhaAntiga(
  legado: unknown,
  catalogo?: readonly DefinicaoDeColuna[],
): EstadoTabela | null {
  if (!Array.isArray(legado)) return null
  return adotarTabela(
    {
      v: 1,
      cols: legado.map((c: { key?: string; on?: boolean }) => ({ k: c?.key, on: c?.on })),
      ord: null,
    },
    catalogo,
  )
}

/**
 * Move uma coluna um passo. O Título fica preso no primeiro lugar, e nada entra
 * antes dele: a linha perderia a sua âncora.
 */
export function moverColuna(
  cols: readonly EstadoColuna[],
  k: ColunaKey,
  passo: -1 | 1,
): EstadoColuna[] {
  const i = cols.findIndex((c) => c.k === k)
  const j = i + passo
  if (i < 1 || j < 1 || j >= cols.length) return [...cols]
  const copia = [...cols]
  const [movida] = copia.splice(i, 1)
  copia.splice(j, 0, movida!)
  return copia
}

/** Largada do arraste: a coluna levada ocupa o lugar da coluna onde soltou. */
export function moverParaOLugarDe(
  cols: readonly EstadoColuna[],
  origem: ColunaKey,
  destino: ColunaKey,
): EstadoColuna[] {
  if (origem === destino || destino === 'titulo' || origem === 'titulo') return [...cols]
  const i = cols.findIndex((c) => c.k === origem)
  const j = cols.findIndex((c) => c.k === destino)
  if (i < 1 || j < 1) return [...cols]
  const copia = [...cols]
  const [movida] = copia.splice(i, 1)
  copia.splice(j, 0, movida!)
  return copia
}

/** Próximo estado do clique no cabeçalho: crescente, decrescente, nenhuma.
 *
 *  Três estados, e não dois, porque desfazer a ordenação é tão legítimo quanto
 *  aplicá-la — com dois, quem ordenou sem querer não tem como voltar. */
export function proximaOrdenacao(atual: Ordenacao, k: ColunaKey): Ordenacao {
  if (atual === null || atual.k !== k) return { k, dir: 'asc' }
  return atual.dir === 'asc' ? { k, dir: 'desc' } : null
}

/**
 * As faixas da grade: uma por coluna ligada, na largura que a pessoa deixou,
 * mais uma faixa livre no fim para a linha chegar à borda quando sobra espaço.
 */
export function trilhosDaGrade(ligadas: readonly EstadoColuna[]): string {
  return [...ligadas.map((c) => `${c.w}px`), '1fr'].join(' ')
}

/**
 * Comparador da coluna ordenada.
 *
 * VAZIO vai para o fim nas duas direções. Inverter a direção não pode fazer
 * "sem prazo" virar o mais urgente: um item sem data não é nem o mais cedo nem
 * o mais tarde, é outra coisa.
 *
 * Empate cai na ordem natural (o código do item), para a lista não embaralhar
 * sozinha a cada redesenho.
 */
export function fazerComparador<T>(
  ordenacao: Ordenacao,
  valorDe: (row: T, k: ColunaKey) => string | number | null,
  codigoDe: (row: T) => string,
): (a: T, b: T) => number {
  const porCodigo = (a: T, b: T) =>
    codigoDe(a).localeCompare(codigoDe(b), 'pt-BR', { numeric: true })
  if (ordenacao === null) return porCodigo
  const sinal = ordenacao.dir === 'asc' ? 1 : -1
  return (a, b) => {
    const va = valorDe(a, ordenacao.k)
    const vb = valorDe(b, ordenacao.k)
    const vazioA = va === null || va === ''
    const vazioB = vb === null || vb === ''
    if (vazioA && vazioB) return porCodigo(a, b)
    if (vazioA) return 1
    if (vazioB) return -1
    const diferenca =
      typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), 'pt-BR', { numeric: true, sensitivity: 'base' })
    return diferenca === 0 ? porCodigo(a, b) : diferenca * sinal
  }
}
